import { MapPin, Box, AlertTriangle } from "lucide-react";

/* WHERE IT IS — ONE PIECE, EVERY SCREEN.
   His words, 2026-10-17: "Fix it so 'where it is' ALWAYS shows the FULL location, easy to read —
   site > area > aisle > rack > shelf — and if it's inside a container, the container too."
   The server sends the chain it computed (segments / full_label / where_text / container / placed);
   this renders it the same way everywhere, at four sizes, and NEVER truncates it. */

// BIG AND EASY TO READ — his instruction, 2026-10-17: *"a large, clear font, readable at arm's
// length on the floor AND at a glance on a PC, without having to look for it."* Every size went up
// a step, the last step of the path is the heaviest thing in the line, and nothing is ever clipped.
const SIZES = {
  // floor tablet: readable at arm's length, across the room
  xl: { wrap: "gap-2", icon: "w-8 h-8", path: "text-3xl sm:text-4xl leading-tight", leaf: "font-black",
        code: "text-base", chip: "text-2xl px-2.5 py-1" },
  // a part page on a PC: seen at a glance, not hunted for
  xlPc: { wrap: "gap-2", icon: "w-6 h-6", path: "text-xl sm:text-2xl leading-tight", leaf: "font-black",
          code: "text-sm", chip: "text-xl px-2 py-0.5" },
  lg: { wrap: "gap-2", icon: "w-5 h-5", path: "text-lg leading-snug", leaf: "font-black",
        code: "text-xs", chip: "text-lg px-2 py-0.5" },
  // lists and tables on a PC
  md: { wrap: "gap-1.5", icon: "w-4 h-4", path: "text-base leading-snug", leaf: "font-bold",
        code: "text-[11px]", chip: "text-sm px-1.5 py-0.5" },
  // dense rows — still a step up from the 11px it used to be
  sm: { wrap: "gap-1", icon: "w-3.5 h-3.5", path: "text-[13px] leading-snug", leaf: "font-bold",
        code: "text-[11px]", chip: "text-[12px] px-1.5 py-0" },
};

const segmentsOf = (loc) => {
  if (!loc) return [];
  if (Array.isArray(loc.segments) && loc.segments.length) return loc.segments;
  // Older payloads (and the location pickers) only carry the breadcrumb string.
  const label = loc.full_label || loc.path_label || loc.name || loc.short_code || "";
  return String(label).split(" > ").filter(Boolean).map((name, i, a) => ({
    name, type: i === a.length - 1 ? (loc.type || null) : null,
    short_code: i === a.length - 1 ? loc.short_code : null,
    is_container: i === a.length - 1 ? (loc.type === "container" || !!loc.is_lot_crate) : false,
  }));
};

/** The whole place as one plain sentence — for printables, CSV, toasts and title attributes. */
export const whereText = (loc) => {
  if (!loc) return "";
  if (loc.where_text) return loc.where_text;
  const segs = segmentsOf(loc);
  if (!segs.length) return loc.short_code || "";
  const names = segs.map((s) => (s.is_container && s.short_code ? s.short_code : s.name));
  const inner = [...names].reverse();
  if (segs[segs.length - 1].is_container) {
    return inner.length === 1 ? `in ${inner[0]} — not placed anywhere yet`
      : `in ${inner[0]} on ${inner.slice(1).join(", ")}`;
  }
  return `on ${inner.join(", ")}`;
};

/** The breadcrumb, site first — for exports and anywhere a single string is needed. */
export const fullLabel = (loc) => {
  if (!loc) return "";
  if (loc.full_label) return loc.full_label;
  return segmentsOf(loc).map((s) => (s.is_container && s.short_code ? s.short_code : s.name)).join(" > ")
    || loc.path_label || loc.short_code || "";
};

export const WhereItIs = ({ location, size = "md", icon = true, testid, className = "",
                            fallback = "No location recorded", showCode = true }) => {
  const s = SIZES[size] || SIZES.md;
  if (!location) {
    return (
      <span data-testid={testid} className={`inline-flex items-center ${s.wrap} text-slate-400 ${s.path} ${className}`}>
        {icon && <MapPin className={`${s.icon} shrink-0`} />}{fallback}
      </span>
    );
  }
  const segs = segmentsOf(location);
  const box = segs.filter((x) => x.is_container).slice(-1)[0] || location.container || null;
  const places = segs.filter((x) => !x.is_container);
  const leaf = places[places.length - 1];
  const unplaced = location.placed === false || (!!box && places.length === 0);
  // Never print the same code twice: when the crate IS the whole place, the amber chip already said it.
  const boxCode = box ? (box.short_code || box.name) : null;
  const rawCode = leaf?.short_code || location.short_code || null;
  const trailingCode = rawCode && rawCode !== boxCode ? rawCode : null;
  return (
    <span data-testid={testid}
      className={`inline-flex flex-wrap items-baseline ${s.wrap} min-w-0 break-words ${className}`}
      title={whereText(location)}>
      {icon && (box
        ? <Box className={`${s.icon} shrink-0 text-amber-600 self-center`} />
        : <MapPin className={`${s.icon} shrink-0 text-emerald-600 self-center`} />)}
      {box && (
        <span data-testid={testid ? `${testid}-container` : undefined}
          className={`font-mono font-bold rounded bg-amber-100 text-amber-900 border border-amber-300 ${s.chip} whitespace-nowrap`}>
          {box.short_code || box.name}
        </span>
      )}
      {places.length > 0 && (
        /* Wraps BETWEEN steps, never mid-word: each step is its own inline-block and the ›
           carries real spaces, so a 390px phone breaks the chain at a step boundary. */
        <span data-testid={testid ? `${testid}-path` : undefined}
          className={`${s.path} text-slate-700 min-w-0 max-w-full break-words`}>
          {places.map((p, i) => (
            <span key={`${p.id || p.name}-${i}`} className="inline-block">
              {i > 0 && <span className="text-slate-500"> › </span>}
              <span className={i === places.length - 1 ? `${s.leaf} text-slate-900` : ""}>{p.name}</span>
            </span>
          ))}
        </span>
      )}
      {unplaced && (
        <span data-testid={testid ? `${testid}-unplaced` : undefined}
          className={`inline-flex items-center gap-1 ${s.path} font-semibold text-[#B01E28]`}>
          <AlertTriangle className={`${s.icon} shrink-0`} />not placed anywhere yet
        </span>
      )}
      {showCode && trailingCode && (
        <span className={`font-mono ${s.code} text-slate-600 whitespace-nowrap`}>
          {trailingCode}
        </span>
      )}
    </span>
  );
};

export default WhereItIs;
